import { pageTitle } from '@palaccount/config';
import { BILL_MESSAGES, type BillResponse, type TwoWayAmount } from '@palaccount/contracts';
import {
  CheckCircle2,
  CreditCard,
  Loader2,
  Plus,
  Send,
  ShieldAlert,
  Trash2,
  TriangleAlert,
  Undo2,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { LastPriceHint } from '@/features/documents/last-price-hint';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useExpenseAccountOptions } from '@/features/accounts/api';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { useRateWarningGate } from '@/features/documents/fx-panel';
import { useRateInput } from '@/features/documents/rate-field';
import { purchaseInvoiceDocument } from '@/features/documents/record-documents';
import { ItemCell } from '@/features/inventory/item-cell';
import {
  billHeaderCreateBody,
  billHeaderPatchBody,
  billHeaderView,
  LineUnitSelect,
  SalesHeaderFields,
  salesHeaderFromDoc,
  useSalesHeaderState,
} from '@/features/sales/sales-header-fields';
import { useDocCurrency } from '@/features/documents/doc-currency';
import { CollapsibleHeaderCard } from '@/features/documents/header-card';
import { SupplierBalanceChip } from '@/features/partners/supplier-balance-chip';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { computeTotalsMirror, type TwoWayState } from '@/features/sales/totals-mirror';
import {
  captureBlocks,
  trackingPayload,
  textFromStored,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';
import { useOrgSetting } from '@/features/admin/api';
import { usePaymentTerms } from '@/features/admin/payment-terms-api';
import { fieldLabel } from '@/features/audit/field-label';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useCompanyToday, useDocumentDate } from '@/lib/session';

import {
  useAcceptVariance,
  useBill,
  useBillHistory,
  useCreateBill,
  useDeleteBill,
  useCancelBill,
  usePostBill,
  useUpdateBill,
} from './api';
import { previewDueDate } from './due-date-mirror';
import { matchVerdict } from './po-rules';
import { MatchChip } from './status-chips';

interface GridLine {
  key: number;
  itemId: string;
  /** Wave 4C: the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
  expenseAccountId: string;
  /**
   * Direct-invoice-to-stock (inventory.bill_writes_stock): the per-block serial/
   * batch capture for a tracked product line, keyed like the sales editor's
   * sub-table. Empty and unused unless the setting is on and the item is tracked.
   */
  tracking: CaptureText;
  /**
   * BR-MD-03-14's "already typed" — a fact about the CELL, set by its own
   * `onChange` and by nothing else. The reasoning, and the three cases that
   * rule out the two simpler tests ("is it empty" and "does it still hold
   * what the pick put there"), is written out once on the same field in
   * `sales/invoice-editor-page.tsx`.
   */
  typedDescription: boolean;
  typedUnitPrice: boolean;
}

let nextKey = 1;
const blankLine = (): GridLine => ({
  key: nextKey++,
  itemId: '',
  unitId: '',
  description: '',
  qty: '',
  unitPrice: '',
  discountPct: '',
  expenseAccountId: '',
  tracking: {},
  typedDescription: false,
  typedUnitPrice: false,
});

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

function fromBill(bill: BillResponse): GridLine[] {
  return bill.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    expenseAccountId: line.expenseAccountId ?? '',
    // Bring a saved draft's captured serials/batch back into the sub-table.
    tracking: textFromStored(line),
    // A saved line's text and price are somebody's typed work.
    typedDescription: true,
    typedUnitPrice: true,
  }));
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * PUR-03 on the SAL-00 shared layout: the supplier's bill with the three-way
 * match panel (ordered / received — dashed only while no goods receipt has
 * been posted against the order — / billed and the
 * verdict recomputed every render, BR-PUR-03-14), the supplier's own invoice
 * number (BR-PUR-03-05), the per-line expense account (5010 default), the
 * variance override for a purchasing.approve holder (BR-PUR-03-16, reason
 * required), and Post behind purchasing.approve (BR-PUR-03-03) with the
 * exact amber sentence for everyone else. A POSTED bill is read-only except
 * its notes (R8 via §7.7). Arriving with ?poId= carries the order across
 * (PUR-01 §5c "Create bill").
 */
export function BillEditorPage() {
  // N-16: the COMPANY's today, never the reader's machine clock.
  const today = useCompanyToday();
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { billId } = useParams<{ billId: string }>();
  const [searchParams] = useSearchParams();
  const poId = searchParams.get('poId');
  const isNew = !billId;

  const { data: bill, isPending: loadingBill } = useBill(billId);
  const { options: expenseOptions } = useExpenseAccountOptions();
  const createBill = useCreateBill();
  const updateBill = useUpdateBill(billId ?? '');
  const deleteBill = useDeleteBill(billId ?? '');
  const postBill = usePostBill(billId ?? '');
  const cancelBill = useCancelBill(billId ?? '');
  const acceptVariance = useAcceptVariance(billId ?? '');

  // `canEdit` still answers a FIELD question — BR-PERM-21 wants a document a
  // person may not change to be genuinely inert, not merely grey. The BUTTON
  // questions moved to <Gated>, which greys rather than hides (BR-PERM-18).
  const canEdit = useCan(isNew ? 'purchasing.create' : 'purchasing.edit');
  const canApprove = useCan('purchasing.approve');

  const [partnerId, setPartnerId] = useState('');
  const [invoiceDate, setInvoiceDate] = useDocumentDate();
  const [dueDate, setDueDate] = useState('');
  /**
   * Wave 1C — BR-MD-09-03's "stays changeable by hand": whether the reader
   * TYPED the due date. While they have not, the box follows the bill date
   * through the supplier's term (previewed here on a new bill, re-derived by
   * the server on a saved draft, which is why the save then OMITS the field).
   */
  const [dueDateTyped, setDueDateTyped] = useState(false);
  /** Wave 1C — the approver's explicit say-so to post before anything arrived. */
  const [unreceivedAsk, setUnreceivedAsk] = useState(false);
  const [supplierRef, setSupplierRef] = useState('');
  /** Header parity (2026-10-30): the sales header's cells; a new bill's purchaser is me. */
  const [header, setHeader] = useSalesHeaderState(isNew);
  const [lines, setLines] = useState<GridLine[]>([blankLine()]);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [tax, setTax] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  /**
   * Direct-invoice-to-stock: ONE receiving warehouse for the whole bill, applied
   * to every product line when the bill posts stock. Pre-filled with the default
   * warehouse; stored per-line under the hood so it stays flexible later.
   */
  const [receivingWarehouseId, setReceivingWarehouseId] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** G-10 — the server's non-blocking duplicate warnings from the last write. */
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  /** GEN-72's ask-why dialog for the posted-bill cancel. */
  const [cancelAsk, setCancelAsk] = useState(false);
  /** BR-PUR-03-02's required acceptance reason, same dialog pattern. */
  const [acceptAsk, setAcceptAsk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  // A ref, not state: StrictMode double-invokes effects, and two concurrent
  // creates would race the per-tenant bill number.
  const carriedFromPo = useRef(false);
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [partnerId, invoiceDate, dueDate, supplierRef, header, lines, discount, tax, notes, receivingWarehouseId],
    isNew ? 'new' : bill && loadedId.current === `${bill.id}:${bill.createdAt}:${bill.status}` ? loadedId.current : null,
  );

  const status = bill?.status ?? 'draft';
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('bills.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (bill && loadedId.current !== `${bill.id}:${bill.createdAt}:${bill.status}`) {
      loadedId.current = `${bill.id}:${bill.createdAt}:${bill.status}`;
      setPartnerId(bill.partnerId);
      setInvoiceDate(bill.invoiceDate);
      setDueDate(bill.dueDate);
      setDueDateTyped(false);
      setSupplierRef(bill.supplierInvoiceNo ?? '');
      setHeader(salesHeaderFromDoc(billHeaderView(bill)));
      setLines(fromBill(bill));
      setReceivingWarehouseId(bill.lines.find((line) => line.warehouseId)?.warehouseId ?? '');
      setDiscount({ basis: 'pct', value: bill.discountPct });
      setTax({ basis: 'pct', value: bill.taxPct });
      setNotes(bill.notes ?? '');
    }
  }, [bill]);

  /**
   * Q-FIN-04-6's cousin on the bill side: arriving from a PO's "Create bill"
   * the order is carried across by the SERVER (the create names the poId and
   * omits the lines). The screen creates the draft immediately and lands on
   * it, so the user edits a carried document rather than retyping one.
   */
  useEffect(() => {
    if (!isNew || poId === null || carriedFromPo.current) return;
    carriedFromPo.current = true;
    void (async () => {
      setBusy(true);
      try {
        const created = await createBill.mutateAsync({
          purchaseOrderId: poId,
          invoiceDate: today,
        });
        void navigate(`/purchases/bills/${created.id}`, { replace: true });
      } catch (thrown) {
        if (thrown instanceof ApiError && thrown.status === 403)
          denyPermission('purchasing.create');
        setError(localizeServerCause(t, thrown, 'bills.errSaveFailed'));
      } finally {
        setBusy(false);
      }
    })();
  }, [isNew, poId]); // deliberately narrow: run once per arrival

  // GEN-50 — one panel of matches, plus the records these lines already name.
  const supplierPicker = usePartnerPicker(partnerId || null, { type: 'supplier' });
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active' },
  );

  // Direct-invoice-to-stock (owner ruling 27 Sep 2026). Off by default; when on,
  // a standalone bill's product lines go straight into stock at posting.
  const billWritesStock = useOrgSetting<boolean>('inventory.bill_writes_stock') === true;
  // Only relevant on a standalone bill — an order-backed one is received by a GRN.
  const stockCapture = billWritesStock && (bill?.purchaseOrderId ?? null) === null && poId === null;
  const { data: warehouses } = useWarehouses();
  const activeWarehouses = useMemo(
    () => (warehouses ?? []).filter((warehouse) => warehouse.isActive),
    [warehouses],
  );
  // Pre-fill the receiving warehouse with the tenant's default (or the only one).
  useEffect(() => {
    if (!stockCapture || receivingWarehouseId !== '' || activeWarehouses.length === 0) return;
    const preferred =
      activeWarehouses.find((warehouse) => warehouse.isDefault) ??
      (activeWarehouses.length === 1 ? activeWarehouses[0] : undefined);
    if (preferred) setReceivingWarehouseId(preferred.id);
  }, [stockCapture, activeWarehouses, receivingWarehouseId]);

  /** OQ-05-17's capture blocks for a line: none for an untracked item, one per tracked unit/component. */
  const blocksFor = (line: GridLine) =>
    captureBlocks(itemPicker.byId.get(line.itemId) as TrackedItem | undefined, line.qty);
  const setTracking = (key: number, blockKey: string, value: string) => {
    setLines((current) =>
      current.map((line) =>
        line.key === key ? { ...line, tracking: { ...line.tracking, [blockKey]: value } } : line,
      ),
    );
  };

  const supplier = supplierPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(bill);
  // Per-document currency (2026-10-30): the header's dropdown sits in front
  // of the chain above; a pick clears the typed rate (it was the old currency's).
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: bill?.currency,
    partnerCurrency: supplier?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;
  /** A bill from a purchase order keeps the order's currency (BR-PUR-03-07). */
  const currencyLocked = !editable || (bill?.purchaseOrderId ?? null) !== null || poId !== null;
  /** The bill seen through the shared header's names (purchaser in the person slot). */
  const billView = useMemo(() => (bill ? billHeaderView(bill) : undefined), [bill]);

  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  // MD-09 §9 — off means the Due date field is not drawn at all.
  const paymentTermsOn = useOrgSetting<boolean>('invoicing.payment_terms');
  // Wave 1C — the company's two purchasing rows: the match's tolerance band
  // (BR-PUR-03-12) and the due-date fallback for a supplier with no term.
  const tolerancePct = useOrgSetting<string>('purchasing.bill_variance_tolerance_pct');
  const defaultTermDays = useOrgSetting<number>('purchasing.default_payment_term_days');
  const { data: paymentTerms } = usePaymentTerms();
  const totals = useMemo(
    () => computeTotalsMirror(lines, discount, tax, currency, defaultTaxPct),
    [lines, discount, tax, currency, defaultTaxPct],
  );

  /**
   * Wave 1C — BR-MD-09-03 on the NEW form: the due date is worked out from
   * the supplier's term the moment the supplier is picked (or the bill date
   * moves), and stays changeable by hand. Only while untyped and only on a
   * new bill: a saved draft's re-derivation is the server's (the save omits
   * the field when it was not typed, and the server recomputes).
   */
  useEffect(() => {
    if (!isNew || dueDateTyped || !paymentTermsOn) return;
    if (!supplier) return;
    const term =
      supplier.paymentTermId === null
        ? null
        : ((paymentTerms ?? []).find((candidate) => candidate.id === supplier.paymentTermId) ?? null);
    // A supplier naming a term the list has not delivered yet: wait for it
    // rather than previewing the fallback for a moment and then jumping.
    if (supplier.paymentTermId !== null && term === null) return;
    setDueDate(
      previewDueDate({
        docDate: invoiceDate,
        term: term === null ? null : { method: term.method, days: term.days, dayOfMonth: term.dayOfMonth },
        paymentTermsOn,
        fallbackDays: defaultTermDays,
      }),
    );
  }, [isNew, dueDateTyped, paymentTermsOn, supplier, paymentTerms, invoiceDate, defaultTermDays]);

  // BR-PUR-03-14: the verdict is recomputed on every render and outranks
  // anything stored. Once a posted receipt exists the RECEIVED leg joins
  // the comparison — the server owns that arithmetic (partial billing,
  // FIFO relief), so its verdict is shown; the client mirror covers the
  // no-receipt case between keystrokes, against what is still OPEN on the
  // order (wave 1C: the bills already posted on it are subtracted) and
  // within the company's tolerance.
  const liveMatch = bill
    ? bill.receivedTotal !== null
      ? bill.matchStatus
      : matchVerdict(
          isDraft ? (totals.grandTotal ?? bill.grandTotal) : bill.grandTotal,
          bill.orderedOpen,
          currency,
          tolerancePct,
        )
    : 'no_order';
  const varianceBlocksPost = liveMatch === 'variance' && !bill?.varianceAccepted;
  /**
   * Wave 1C — stock items on a sent order nothing has been received against:
   * the server refuses to post (`BILL_REFUSALS.nothingReceived`) unless the
   * approver says so explicitly. The question is asked HERE, before the
   * round trip, in the product's one shared box — and only to a holder of
   * purchasing.approve, who is the only person the server would listen to.
   */
  const unreceivedStock =
    bill !== undefined &&
    bill.purchaseOrderId !== null &&
    bill.receivedTotal === null &&
    bill.hasStockLines;

  const setLine = (key: number, patch: Partial<GridLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /** BR-MD-03-14: picking fills name and price; it never overwrites typed work. */
  const onPickItem = (key: number, itemId: string) => {
    const item = itemPicker.byId.get(itemId);
    if (!item) {
      setLine(key, { itemId: '' });
      return;
    }
    const price =
      item.costPrice && item.costPrice !== '0.00' ? item.costPrice : (item.unitPrice ?? '');
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              itemId,
              // The picked unit belonged to the OLD item.
              unitId: '',
              // Per FIELD, not per line: a typed description must not stop
              // the untouched price filling.
              description: line.typedDescription ? line.description : descLang.describe(item),
              unitPrice: line.typedUnitPrice ? line.unitPrice : price,
            }
          : line,
      ),
    );
  };

  const removeLine = (key: number) => {
    setLines((current) => {
      const remaining = current.filter((line) => line.key !== key);
      return remaining.length === 0 ? [blankLine()] : remaining;
    });
  };

  const usedLines = lines.filter(
    (line) =>
      line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
  );

  const validate = (): string | null => {
    if (!partnerId) return t('bills.errNoSupplier');
    if (usedLines.length === 0) return t('sales.errNoLines');
    for (const [index, line] of usedLines.entries()) {
      if (line.description.trim() === '') return t('sales.errLineDescription', { line: index + 1 });
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim()))
        return t('sales.errLineQty', { line: index + 1 });
      if (!MONEY_RE.test(line.unitPrice.trim())) return t('sales.errLinePrice', { line: index + 1 });
      const pct = line.discountPct.trim();
      if (pct !== '' && !PCT_RE.test(pct)) return t('sales.errDiscountRange');
    }
    if (discount && !(discount.basis === 'pct' ? PCT_RE : MONEY_RE).test(discount.value.trim()))
      return t('sales.errDiscountRange');
    if (tax && !(tax.basis === 'pct' ? PCT_RE : MONEY_RE).test(tax.value.trim()))
      return t('sales.errTaxRange');
    return null;
  };

  const buildTwoWay = (state: TwoWayState | undefined): TwoWayAmount | undefined => {
    if (!state) return undefined;
    return state.basis === 'pct' ? { pct: state.value.trim() } : { amount: state.value.trim() };
  };

  const requestLines = () =>
    usedLines.map((line) => ({
      ...(line.itemId ? { itemId: line.itemId } : {}),
      ...(line.itemId && line.unitId ? { unitId: line.unitId } : {}),
      description: line.description.trim(),
      qty: line.qty.trim(),
      unitPrice: line.unitPrice.trim(),
      ...(line.discountPct.trim() !== '' ? { discountPct: line.discountPct.trim() } : {}),
      ...(line.expenseAccountId ? { expenseAccountId: line.expenseAccountId } : {}),
      // Direct-invoice-to-stock: the whole bill's receiving warehouse rides on
      // each product line, and the identifiers travel with the DRAFT so a
      // half-scanned line survives a save (BR-MD-03 capture, sales-invoice shape).
      ...(stockCapture && line.itemId && receivingWarehouseId
        ? { warehouseId: receivingWarehouseId }
        : {}),
      ...(stockCapture && line.itemId ? trackingPayload(blocksFor(line), line.tracking) : {}),
    }));

  type PurchPermission =
    | 'purchasing.create'
    | 'purchasing.edit'
    | 'purchasing.delete'
    | 'purchasing.approve';
  const handleFailure = (thrown: unknown, permission: PurchPermission) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`BILL_MESSAGES` are
    // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
    // screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'bills.errSaveFailed'));
  };

  /** Wave 4C — lines from a small .xlsx/.csv, priced at the item's cost; unknown codes stay free text. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'bill',
    side: 'purchase',
    describe: descLang.describe,
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    visible: isDraft,
    gate: {
      permission: isNew ? 'purchasing.create' : 'purchasing.edit',
      reasonKey: isNew ? 'perm.cannot.createBills' : 'perm.cannot.editBills',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setLines((current) => [
        ...current.filter(
        (line) =>
          line.itemId !== '' ||
          line.description.trim() !== '' ||
          line.qty.trim() !== '' ||
          line.unitPrice.trim() !== '',
      ),
        ...imported.map((line) => ({
          ...blankLine(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          typedDescription: true,
          typedUnitPrice: true,
        })),
      ]),
  });

  const save = async (navigateAfter = true): Promise<BillResponse | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const created = await createBill.mutateAsync({
          ...rate.createBody(),
          ...docCurrency.body(),
          ...billHeaderCreateBody(header),
          partnerId,
          invoiceDate,
          ...(dueDate ? { dueDate } : {}),
          ...(supplierRef.trim() ? { supplierInvoiceNo: supplierRef.trim() } : {}),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        setWarnings(created.warnings ?? []);
        if (navigateAfter) void navigate(`/purchases/bills/${created.id}`, { replace: true });
        return created;
      }
      // Wave 1C: a due date the reader did not type is the SERVER's to
      // re-derive when the bill date moved (BR-MD-09-03) — so it is omitted
      // exactly then. A typed one, or an unmoved date, is sent as before.
      const dateMoved = bill !== undefined && invoiceDate !== bill.invoiceDate;
      const sendDueDate = dueDate !== '' && (dueDateTyped || !dateMoved);
      const updated = await updateBill.mutateAsync({
        ...rate.patchBody(),
        ...docCurrency.body(),
        ...billHeaderPatchBody(header),
        invoiceDate,
        ...(sendDueDate ? { dueDate } : {}),
        supplierInvoiceNo: supplierRef.trim() === '' ? null : supplierRef.trim(),
        lines: requestLines(),
        ...(discount ? { discount: buildTwoWay(discount)! } : {}),
        ...(tax ? { tax: buildTwoWay(tax)! } : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      setWarnings(updated.warnings ?? []);
      loadedId.current = null;
      return updated;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'purchasing.create' : 'purchasing.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  const onPost = async (options: { allowUnreceived?: boolean } = {}) => {
    // Wave 1C: ask the approver first — GEN-208, the click opens the
    // question and does nothing else; the confirm re-enters with the flag.
    if (unreceivedStock && options.allowUnreceived !== true) {
      setUnreceivedAsk(true);
      return;
    }
    // BR-MD-11-14 at the confirming click (the same question the sales invoice
    // asks), before the save that precedes the post.
    if (!(await confirmRateWarning(bill?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const created = await save(false);
        if (!created) return;
        const response = await api.purchaseInvoices.post({
          params: { billId: created.id },
          body: {},
        });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('purchasing.approve');
          const body = response.body;
          setError(
            localizeServerMessage(
              t,
              typeof body === 'object' && body !== null && 'message' in body
                ? String((body as { message: unknown }).message)
                : t('common.loadFailed'),
            ),
          );
          void navigate(`/purchases/bills/${created.id}`, { replace: true });
          return;
        }
        void navigate(`/purchases/bills/${created.id}`, { replace: true });
        return;
      }
      const saved = await save();
      if (!saved) return;
      const posted = await postBill.mutateAsync(
        options.allowUnreceived === true ? { allowUnreceived: true } : {},
      );
      setWarnings(posted.warnings ?? []);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  /** BR-PUR-03-16: the override, with its required reason (BR-PUR-03-02) —
   * collected in the ReasonBox (ADM-02 §8: no browser chrome). */
  const onAcceptVariance = async (reason: string) => {
    if (!billId) return;
    setAcceptAsk(false);
    setBusy(true);
    setError(null);
    try {
      await acceptVariance.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!billId) return;
    setDeleting(false);
    setBusy(true);
    setError(null);
    try {
      await deleteBill.mutateAsync();
      void navigate('/purchases/bills');
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.delete');
    } finally {
      setBusy(false);
    }
  };

  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateBill.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-PUR-03-11 / GEN-72: cancelling a posted bill asks WHY — the
   * ReasonBox IS the confirm (ADM-02 §8: no browser chrome). Refused
   * server-side while a payment is matched ("unmatch it first") or a debit
   * note stands (GEN-74, named).
   */
  const onCancelPosted = async (reason: string) => {
    if (!billId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelBill.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingBill) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the post hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('bills.editorTitle')}</h2>
        {/* BR-SAL-00-02's naming half, on the shared chip — "Bill No.". */}
        <DocumentNumberChip
          label={t('bills.colNo')}
          code={bill?.code ?? null}
          fallback={t('bills.codeNew')}
        />
        <DocStatusChip status={status} />
        {bill ? <MatchChip status={liveMatch} /> : null}
        {bill?.purchaseOrderCode ? (
          <Link
            to={`/purchases/orders/${bill.purchaseOrderId}`}
            className="rounded-full border px-2.5 py-0.5 font-mono text-xs text-primary hover:underline"
          >
            <bdi dir="ltr">{bill.purchaseOrderCode}</bdi>
          </Link>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'bill', id: billId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row — a link back to the list. The act
              // the table means is `bills.cancelPosted`, four rows below.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/purchases/bills',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the bill. Saved
              // bills only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && bill ? (
                  <DocumentActions
                    request={{ kind: 'purchase_invoice', locale: printLocale, subjectId: bill.id }}
                    payload={{ kind: 'purchase_invoice', data: purchaseInvoiceDocument(bill) }}
                  />
                ) : null,
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              // BR-PERM-18, and 16.7's own worked example: "Your role cannot
              // delete bills." Shown and greyed, never hidden — see
              // components/permission-gate.tsx for why this screen is the one
              // that demonstrates the pattern. Whether the button EXISTS is
              // still a lifecycle question (a posted bill has no Delete at
              // all, BR-PERM-02); whether it is USABLE is the permission
              // question. Two fields, because two questions.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'purchasing.delete', reasonKey: 'perm.cannot.deleteBills' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // PUR-03 §5c "Record payment" — hidden while a variance shows
              // (BR-PUR-03-01; an unaccepted-variance bill cannot be posted,
              // so reaching here means the match passed or was overridden).
              id: 'recordPayment',
              row: null,
              label: t('bills.recordPayment'),
              icon: <CreditCard aria-hidden />,
              visible: status === 'posted' && !bill?.fullyPaid,
              to: `/accounting/payments/new?partnerId=${bill?.partnerId}&billId=${billId}&currency=${bill?.currency ?? ''}`,
            },
            {
              // PUR-04 §3 — "Debit this bill" opens the Purchase Return screen
              // with the bill already linked. A navigation, not a type switch
              // (BR-SAL-00-28): the return is its own screen on its own route.
              id: 'debit',
              row: null,
              label: t('bills.debitThisBill'),
              icon: <Undo2 aria-hidden />,
              variant: 'outline',
              visible: status === 'posted',
              to: `/purchases/returns/new?billId=${billId}`,
            },
            {
              // BR-PUR-03-11 — "It may only be cancelled, which writes the
              // reversing entry." A posting act, gated exactly like Post.
              // THIS is PART 4.6's Cancel row: it marks a confirmed record as
              // no longer valid and undoes its accounting.
              id: 'cancelPosted',
              row: 'cancel',
              label: t('bills.cancelPosted'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted',
              gate: { permission: 'purchasing.approve', reasonKey: 'perm.cannot.cancelBills' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              // Save exists while the DOCUMENT is editable (a posted bill is
              // frozen, BR-PERM-02) and is greyed when the ROLE is not — the
              // two questions 16.7 keeps apart, kept apart here.
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isDraft,
              gate: {
                permission: isNew ? 'purchasing.create' : 'purchasing.edit',
                reasonKey: isNew ? 'perm.cannot.createBills' : 'perm.cannot.editBills',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('bills.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* GEN-59's typed-reason box. UNGUARDED, as it already was: unlike
              its siblings this one sits outside the `status === 'posted'`
              condition, and it is left that way because moving it would be a
              behaviour change dressed as tidying. */}
          <ReasonBox
            open={cancelAsk}
            title={t('bills.cancelPosted')}
            prompt={t('bills.cancelPrompt')}
            reasonLabel={t('gen72.cancelReasonLabel')}
            confirmLabel={t('gen72.confirmCancel')}
            busy={busy}
            onConfirm={(reason) => void onCancelPosted(reason)}
            onCancel={() => setCancelAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('bills.postedNotice')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('bills.cancelledNotice', { reason: bill?.cancelReason ?? '' })}
        </p>
      ) : null}

      {/* G-10 — possible duplicate, said once per write and never refused.
          The server's sentence is matched to this screen's key (the two
          BILL_MESSAGES warnings are fixed sentences), so an Arabic reader
          gets Arabic; an unknown sentence still shows rather than vanishes. */}
      {warnings.map((warning) => (
        <p
          key={warning}
          role="status"
          className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          <span>
            {warning === BILL_MESSAGES.duplicateRefWarning
              ? t('bills.warnDuplicateRef')
              : warning === BILL_MESSAGES.duplicateTotalWarning
                ? t('bills.warnDuplicateTotal')
                : localizeServerMessage(t, warning)}
          </span>
        </p>
      ))}

      {/* The variance banner + override (BR-PUR-03-01/-16). */}
      {isDraft && liveMatch === 'variance' && !bill?.varianceAccepted ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          <span className="me-auto">{t('bills.varianceBanner')}</span>
          {!isNew ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.acceptVariance">
              <Button size="sm" variant="outline" onClick={() => setAcceptAsk(true)} disabled={busy}>
                {t('bills.acceptVariance')}
              </Button>
            </Gated>
          ) : null}
          <ReasonBox
            open={acceptAsk}
            title={t('bills.acceptVariance')}
            prompt={t('bills.acceptReasonPrompt')}
            reasonLabel={t('bills.acceptReasonLabel')}
            confirmLabel={t('bills.acceptConfirm')}
            busy={busy}
            onConfirm={(reason) => void onAcceptVariance(reason)}
            onCancel={() => setAcceptAsk(false)}
          />
        </div>
      ) : null}
      {bill?.varianceAccepted ? (
        <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
          {t('bills.varianceAccepted', { reason: bill.varianceReason ?? '' })}
        </p>
      ) : null}
      {/* Wave 1C — goods on a sent order, nothing received: said up front,
          so the question at Post is not a surprise. */}
      {isDraft && unreceivedStock ? (
        <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          {t('bills.unreceivedNotice')}
        </p>
      ) : null}
      {/* The approver's explicit say-so, in the product's ONE shared box
          (BR-PERM-15). Inside the same condition as the banner, so the dialog
          cannot outlive the state that justifies it; only a purchasing.approve
          holder ever reaches it, because only Post opens it. */}
      {isDraft && unreceivedStock && canApprove ? (
        <ConfirmBox
          open={unreceivedAsk}
          title={t('bills.confirmUnreceivedTitle')}
          body={t('bills.confirmUnreceivedBody', { code: bill?.purchaseOrderCode ?? '' })}
          confirmLabel={t('bills.confirmUnreceivedAct')}
          busy={busy}
          onConfirm={() => {
            setUnreceivedAsk(false);
            void onPost({ allowUnreceived: true });
          }}
          onCancel={() => setUnreceivedAsk(false)}
        />
      ) : null}
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── The details line — the sales invoice's box, foldable (2026-10-30) ── */}
      <CollapsibleHeaderCard
        storageKey="bill"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        summary={{
          partner: bill?.partnerName ?? supplier?.name,
          date: invoiceDate,
          currency,
          code: bill?.code,
          status: <DocStatusChip status={status} />,
        }}
      >
          <div className="grid gap-1.5">
            <Label htmlFor="bill-date">{t('common.date')}</Label>
            <Input
              id="bill-date"
              type="date"
              dir="ltr"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* MD-09 §9: with the Payment terms switch off, the Due date field
              does not appear — bills fall due on their bill date. */}
          {paymentTermsOn ? (
            <div className="grid gap-1.5">
              {/* BR-PUR-03-06: never earlier than the bill date (server-checked). */}
              <Label htmlFor="bill-due">{t('sales.fDueDate')}</Label>
              <Input
                id="bill-due"
                type="date"
                dir="ltr"
                value={dueDate}
                onChange={(event) => {
                  setDueDate(event.target.value);
                  setDueDateTyped(true);
                }}
                disabled={!editable}
              />
              {/* Wave 1C: the preview says where the date came from. */}
              {isNew && !dueDateTyped && dueDate !== '' ? (
                <p className="text-xs text-muted-foreground">{t('bills.dueFromTerms')}</p>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <Label htmlFor="bill-supplier">{t('po.fSupplier')}</Label>
            {/* GEN-50: OFFERS active suppliers, DISPLAYS whoever this bill
                already names. */}
            <PartnerTypeahead
              entity="supplier"
              id="bill-supplier"
              label={t('po.fSupplier')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={supplierPicker.options}
              selected={supplierPicker.optionFor(partnerId)}
              query={supplierPicker.query}
              onQueryChange={supplierPicker.setQuery}
              placeholder={t('po.pickSupplier')}
              emptyLabel={t('picker.noMatchingSupplier')}
              loading={supplierPicker.loading}
              disabled={!editable || !isNew}
            />
            {/* The sales invoice's balance chip, buyer-side: what we owe them. */}
            <SupplierBalanceChip partnerId={partnerId || null} side="supplier" className="justify-self-start" />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('po.fSupplierId')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              {/* BR-SAL-00-01's binding, as on the sales invoice: the saved
                  bill's own stored code first, the picked supplier's on a new draft. */}
              <Ltr value={bill?.partnerCode ?? supplier?.code ?? '—'} />
              <span className="ms-auto text-muted-foreground">
                <Ltr value={currency} />
              </span>
            </div>
          </div>
          {/* Direct-invoice-to-stock: where the goods arrive. Pre-filled with the
              default warehouse; only shown when the setting is on and this is a
              standalone bill (an order-backed one is received by a goods receipt). */}
          {stockCapture ? (
            <div className="grid gap-1.5">
              <Label htmlFor="bill-warehouse">{t('bills.fReceivingWarehouse')}</Label>
              <NativeSelect
                id="bill-warehouse"
                value={receivingWarehouseId}
                onChange={(event) => setReceivingWarehouseId(event.target.value)}
                disabled={!editable}
              >
                <option value="">{t('bills.warehousePlaceholder')}</option>
                {activeWarehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ) : null}
          {/* Header parity: project, supplier branch, manual ref, book no,
              purchaser, then currency & rate — the sales header's own cells. */}
          <SalesHeaderFields
            idPrefix="bill"
            side="purchase"
            omit={['warehouse']}
            partnerId={partnerId}
            state={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            disabled={!editable}
            doc={billView}
            rate={rate}
            currency={currency}
            currencyPicker={{ onChange: docCurrency.pick, disabled: currencyLocked }}
            warehouses={warehouses}
          />
          <div className="grid gap-1.5">
            {/* BR-PUR-03-05 — "the most valuable duplicate check in the
                system": required at posting, unique per supplier. */}
            <Label htmlFor="bill-ref">{t('bills.fSupplierRef')}</Label>
            <Input
              id="bill-ref"
              dir="ltr"
              maxLength={60}
              placeholder="SI-77120"
              value={supplierRef}
              onChange={(event) => setSupplierRef(event.target.value)}
              disabled={!editable}
            />
          </div>
      </CollapsibleHeaderCard>

      {/* ── The 3-way match panel (§7.5) ───────────────────────────────── */}
      {bill ? (
        <Card>
          <CardHeader className="flex-row items-center">
            <CardTitle className="me-auto">{t('bills.match')}</CardTitle>
            <MatchChip status={liveMatch} />
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
              <span>{t('bills.matchOrdered')}</span>
              <span className="flex items-center gap-3">
                {bill.purchaseOrderCode ? (
                  <Ltr value={bill.purchaseOrderCode} className="font-mono text-primary" />
                ) : null}
                {bill.orderedTotal !== null ? (
                  <Amount value={bill.orderedTotal} currency={currency} className="font-semibold" />
                ) : (
                  /* Wave 1C: "service bill" only when NO line is a stock item —
                     goods billed with no order and no receipt are something
                     else, and the label says so. */
                  <span className="text-muted-foreground">
                    {bill.hasStockLines ? t('bills.matchNoOrderGoods') : t('bills.matchNoOrder')}
                  </span>
                )}
              </span>
            </div>
            {/* The GRN leg (BR-PUR-02-06): Σ accepted value at the order's
                prices; the dash only while no receipt has posted. */}
            {bill.receivedTotal !== null ? (
              <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                <span>{t('bills.matchReceived')}</span>
                <Amount value={bill.receivedTotal} currency={currency} className="font-semibold" />
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-md border border-dashed border-muted-foreground/40 px-3 py-2 text-muted-foreground">
                <span>{t('bills.matchReceived')}</span>
                <span title={t('bills.receivedDash')}>—</span>
              </div>
            )}
            <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
              <span>{t('bills.matchBilled')}</span>
              <span className="flex items-center gap-3">
                <Ltr value={bill.code} className="font-mono text-primary" />
                <Amount
                  value={isDraft ? (totals.grandTotal ?? bill.grandTotal) : bill.grandTotal}
                  currency={currency}
                  className="font-semibold"
                />
              </span>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* ── The item-linked grid with the per-line expense account ─────── */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="w-40">{t('po.colItem')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[160px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-20 text-end">{t('sales.colQty')}</TableHead>
                <TableHead className="w-28 text-end">{t('sales.colUnitPrice')}</TableHead>
                {/* Wave 1C: the box holds a PERCENTAGE, and the heading says so. */}
                <TableHead className="w-20 text-end">{t('po.colDiscPct')}</TableHead>
                <TableHead className="w-36">{t('bills.colAccount')}</TableHead>
                <TableHead className="w-28 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const view = totals.lines[index];
                return (
                  <Fragment key={line.key}>
                  <TableRow className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <ItemCell
                        label={t('po.cellItem', { line: index + 1 })}
                        clearLabel={t('po.itemFree')}
                        picker={itemPicker}
                        value={line.itemId}
                        onPick={(itemId) => onPickItem(line.key, itemId)}
                        disabled={!editable}
                      />
                      {/* Wave 4C — the supplier's last price for this item, one click to use. */}
                      {partnerId && line.itemId ? (
                        <LastPriceHint
                          side="purchase"
                          partnerId={partnerId}
                          itemId={line.itemId}
                          currency={currency}
                          currentPrice={line.unitPrice}
                          editable={editable}
                          line={index + 1}
                          onUse={(price) => setLine(line.key, { unitPrice: price, typedUnitPrice: true })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {/* Wave 4C — the unit the qty is typed in; '' = the item's stock unit. */}
                      <LineUnitSelect
                        item={line.itemId ? itemPicker.byId.get(line.itemId) : undefined}
                        value={line.unitId}
                        onChange={(unitId) => setLine(line.key, { unitId })}
                        disabled={!editable}
                        label={t('sales.cellUnit', { line: index + 1 })}
                        use="buy"
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        aria-label={t('sales.cellDescription', { line: index + 1 })}
                        value={line.description}
                        onChange={(event) =>
                          setLine(line.key, {
                            description: event.target.value,
                            typedDescription: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellQty', { line: index + 1 })}
                        value={line.qty}
                        onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                        value={line.unitPrice}
                        onChange={(event) =>
                          setLine(line.key, {
                            unitPrice: event.target.value,
                            typedUnitPrice: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellDisc', { line: index + 1 })}
                        value={line.discountPct}
                        placeholder="0"
                        onChange={(event) => setLine(line.key, { discountPct: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Expense accounts only, by design: a goods line never
                          names an inventory account here. Its value reaches
                          inventory through the goods receipt, and posting
                          empties 2115 by what was RECEIVED (BR-PUR-03-04). */}
                      <NativeSelect
                        aria-label={t('bills.cellAccount', { line: index + 1 })}
                        className="h-9 rounded-md font-mono text-xs"
                        value={line.expenseAccountId}
                        onChange={(event) =>
                          setLine(line.key, { expenseAccountId: event.target.value })
                        }
                        disabled={!editable}
                      >
                        <option value="">{t('bills.accountDefault')}</option>
                        {expenseOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.code} — {option.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </TableCell>
                    <TableCell className="text-end">
                      {view?.lineTotal !== null && view?.lineTotal !== undefined ? (
                        <Amount value={view.lineTotal} currency={currency} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          title={t('sales.removeLine', { line: index + 1 })}
                          onClick={() => removeLine(line.key)}
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                  {/* Serial/batch capture: only renders rows for a TRACKED
                      product line on a stock-writing bill; nothing otherwise. */}
                  {stockCapture ? (
                    <TrackingRows
                      blocks={blocksFor(line)}
                      text={line.tracking}
                      onChange={(blockKey, value) => setTracking(line.key, blockKey, value)}
                      disabled={!editable}
                      columns={editable ? 10 : 9}
                      line={index + 1}
                    />
                  ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('sales.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell colSpan={editable ? 5 : 4} className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.subtotal')}
                  </span>{' '}
                  <Amount value={totals.subtotal} currency={currency} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Notes + history, and the summary rail ──────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('sales.notes')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Textarea
                aria-label={t('sales.notes')}
                placeholder={t('bills.notesPh')}
                maxLength={2000}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!editable && !(status === 'posted' && canEdit)}
              />
              {status === 'posted' && canEdit ? (
                <Button variant="outline" className="justify-self-start" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              ) : null}
            </CardContent>
          </Card>

          {/* PUR-03 §5e — the history card, one interface (§9): it renders for
              every user who has the screen, drafts and the not-yet-saved bill
              included — the empty state on a NEW bill is exactly the moment
              the prototype showed the wrong sentence. */}
          <BillHistoryCard billId={billId} />
        </div>

        <Card>
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows (F-2…F-7), with the currency code by the total. */}
            <DocumentTotals
              currency={currency}
              subtotal={totals.subtotal}
              discountAmount={totals.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={totals.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={totals.taxAmount}
              vatControl={
                <TwoWayPair
                  kind="vat"
                  state={tax}
                  onChange={setTax}
                  pct={totals.taxPct ?? '15'}
                  amount={totals.taxAmount ?? ''}
                  disabled={!editable}
                />
              }
              grandTotal={totals.grandTotal}
            />

            <div className="flex items-center justify-between text-sm">
              <span>{t('sales.amountPaid')}</span>
              <Amount value={bill?.amountPaid ?? '0.00'} currency={currency} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>{t('sales.balanceDue')}</span>
              {/* Wave 1C: a cancelled bill owes nothing — the server answers
                  0.00 — and it is neither "paid" (green) nor "owing" (amber). */}
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 font-semibold',
                  status === 'cancelled'
                    ? 'bg-muted text-muted-foreground'
                    : (bill?.fullyPaid ?? false)
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
              >
                <Amount value={bill?.balanceDue ?? totals.grandTotal ?? '0.00'} currency={currency} />
              </span>
            </div>

            {/* The second of BR-SAL-04-09's three purchase strips — the
                FxPanel with the rate and its "as of" date — now sits in the
                header's Currency & rate cell (SalesHeaderFields), exactly where
                the sales invoice draws it (INV-F5). */}

            {/* Post bill — behind purchasing.approve (BR-PUR-03-03); disabled
                while an unaccepted variance shows (BR-PUR-03-01). */}
            {isDraft ? (
              <Gated permission="purchasing.approve" reasonKey="perm.cannot.postBills" className="w-full">
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || !partnerId || usedLines.length === 0 || varianceBlocksPost}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('bills.postBill')}
                </Button>
              </Gated>
            ) : null}
            {/* The banner stays: 16.7 asks the BUTTON to say why on hover, and
                a person who cannot post a bill they are looking at deserves
                the sentence without having to find it with a pointer. */}
            {isDraft && !canApprove ? (
              <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <ShieldAlert className="size-4 shrink-0" aria-hidden />
                {t('bills.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document (the supplier's own paper). Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="purchase_invoice" entityId={billId} />
    </div>
  );
}

/**
 * PUR-03 §5e — the history card: the shared audit log filtered to THIS bill,
 * up to six entries newest first (the server's promise), each drawn as
 * SAL-04 §5e's four-part line — timestamp · action · what changed · who.
 * Two or more changes in one entry join with a middle dot, an empty previous
 * value draws as an em dash, and the arrow is dictionary text (`unit.arrow`)
 * so it points along the reading direction in Arabic (NFR-04).
 */
function BillHistoryCard({ billId }: { billId: string | undefined }) {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useBillHistory(billId);
  const entries = data?.entries ?? [];

  const stamp = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('bills.history')}</CardTitle>
      </CardHeader>
      <CardContent>
        {billId && isPending ? (
          <div className="h-10 animate-pulse rounded-md bg-muted" aria-busy />
        ) : entries.length === 0 ? (
          /* BR-PUR-03-09 / ERR-PUR-03-07 — the empty text names a BILL. The
             prototype borrowed the purchase order's sentence ("No changes
             recorded for this ORDER yet") onto this screen; the card's own
             key says bill in both languages. */
          <p className="text-sm text-muted-foreground">{t('bills.historyEmpty')}</p>
        ) : (
          <ul className="grid gap-1.5 text-sm">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-baseline gap-3">
                <span className="w-24 shrink-0 font-mono text-xs text-muted-foreground">
                  <Ltr value={stamp(entry.occurredAt)} />
                </span>
                <span className="w-[70px] shrink-0 font-semibold">
                  {t(`audit.action.${entry.action}`)}
                </span>
                <span className="min-w-0 flex-1 break-words text-xs">
                  {/* Wave 1C: the field reads as words in the reader's language
                      (`audit.field.*`), never as the API's raw name. */}
                  {entry.changes
                    .map(
                      (change) =>
                        `${fieldLabel(t, change.field)}: ${change.from ?? '—'} ${t('unit.arrow')} ${change.to ?? '—'}`,
                    )
                    .join(' · ')}
                </span>
                <span className="shrink-0 text-end text-xs text-muted-foreground">
                  {entry.actorName ?? entry.actorEmail ?? t('audit.systemActor')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
